import { colors } from '../KestrixDesignSystem.jsx'
import { propertyTableHeaderTokens } from './property-table-header.tokens.ts'
import type { PropertyTableHeaderColumn } from './property-table-header.types.ts'

/** Stable default so pinning derivations are not recomputed on every render. */
export const NO_PINNED_COLUMNS: ReadonlyArray<string> = []

/** Which part of the table a pinned cell belongs to. */
export type PinnedCellVariant = 'head' | 'body'

/** Sticky cell styles. A plain object so callers can spread into `sx`. */
export type PinnedCellSx = {
  readonly position?: 'sticky'
  readonly left?: string | number
  readonly zIndex?: number
  readonly top?: number
  readonly bgcolor?: string
  readonly borderRight?: string
  readonly boxShadow?: string
}

/** Derived geometry shared by every pinned cell in one table. */
export interface PinnedColumnLayout {
  /** Sticky `left` offset per pinned column key. */
  readonly leftOffsets: ReadonlyMap<string, string | number>
  /** Rightmost pinned column, which carries the scroll shadow. */
  readonly lastPinnedColumnKey: string | null
  /** Table min width, or `undefined` when no columns are pinned. */
  readonly tableMinWidth: number | undefined
}

const PINNED_CELL_VARIANT_STYLES = {
  head: {
    zIndex: 3,
    top: 0,
    bgcolor: propertyTableHeaderTokens.surface
  },
  body: { zIndex: 1, bgcolor: colors.background.secondary }
} as const

const formatColumnWidth = (width: string | number): string =>
  typeof width === 'number' ? `${width}px` : width

const addPinnedColumnWidth = (
  offset: string | number,
  width: string | number
): string | number => {
  if (offset === 0) {
    return formatColumnWidth(width)
  }

  return `calc(${offset} + ${formatColumnWidth(width)})`
}

export const isPinnedColumn = (
  column: PropertyTableHeaderColumn,
  pinnedColumnKeys: ReadonlyArray<string>
): boolean => pinnedColumnKeys.includes(column.key)

/**
 * Maps pinned column keys to their sticky `left` offset (sum of prior pinned widths).
 */
export const buildPinnedColumnLeftOffsets = (
  columns: ReadonlyArray<PropertyTableHeaderColumn>,
  pinnedColumnKeys: ReadonlyArray<string>
): ReadonlyMap<string, string | number> => {
  if (pinnedColumnKeys.length === 0) {
    return new Map()
  }

  const offsets = new Map<string, string | number>()
  let pinnedOffset: string | number = 0

  for (const column of columns) {
    if (!isPinnedColumn(column, pinnedColumnKeys)) {
      continue
    }

    offsets.set(column.key, pinnedOffset)
    pinnedOffset = addPinnedColumnWidth(pinnedOffset, column.width)
  }

  return offsets
}

export const getLastPinnedColumnKey = (
  columns: ReadonlyArray<PropertyTableHeaderColumn>,
  pinnedColumnKeys: ReadonlyArray<string>
): string | null => {
  if (pinnedColumnKeys.length === 0) {
    return null
  }

  for (let index = columns.length - 1; index >= 0; index -= 1) {
    const column = columns[index]
    if (column !== undefined && isPinnedColumn(column, pinnedColumnKeys)) {
      return column.key
    }
  }

  return null
}

const parsePxWidth = (width: string | number): number | null => {
  if (typeof width === 'number') {
    return width
  }
  const match = /^(\d+(?:\.\d+)?)px$/.exec(width)
  return match?.[1] === undefined ? null : Number(match[1])
}

/** Minimum table width so horizontal scroll can engage when columns use `%` widths. */
export const resolvePinnedTableMinWidth = (
  columns: ReadonlyArray<PropertyTableHeaderColumn>,
  pinnedColumnKeys: ReadonlyArray<string>
): number | undefined => {
  if (pinnedColumnKeys.length === 0) {
    return undefined
  }

  const pixelWidths = columns.map((column) => parsePxWidth(column.width))
  if (pixelWidths.every((width): width is number => width !== null)) {
    return pixelWidths.reduce((sum, width) => sum + width, 0)
  }

  return propertyTableHeaderTokens.scrollMinWidth
}

/** Sticky styles for a pinned cell, or `{}` when the column is not pinned. */
export const buildPinnedCellSx = (
  columnKey: string,
  layout: PinnedColumnLayout,
  variant: PinnedCellVariant,
  showPinnedShadow = false
): PinnedCellSx => {
  const left = layout.leftOffsets.get(columnKey)
  if (left === undefined) {
    return {}
  }

  const isLastPinned = columnKey === layout.lastPinnedColumnKey

  return {
    position: 'sticky',
    left,
    ...PINNED_CELL_VARIANT_STYLES[variant],
    ...(isLastPinned
      ? {
          borderRight: `1px solid ${propertyTableHeaderTokens.pinnedEdgeBorder}`,
          ...(showPinnedShadow
            ? { boxShadow: propertyTableHeaderTokens.pinnedColumnShadow }
            : {})
        }
      : {})
  }
}
